// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import isNumeric from 'isnumeric';
import { some } from 'lodash';
import { withRouter } from 'react-router-dom';
import { FRIEND_GROUP_ID, NEW_GROUP_ID } from 'app/constants/friendConstants';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import FriendLoader from 'app/components/friends/common/FriendsLoader';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import GroupCardsMobile from 'app/components/friends/groupCardCarousel/GroupCardsMobile';
import AnimatedGroupChild from 'app/components/commons/AnimatedGroupChild';
import GroupCardsDesktop from 'app/components/friends/groupCardCarousel/GroupCardsDesktop';
import Responsive from 'app/components/responsive/Responsive';
import t from 'app/utils/translate';

type Props = {
  areGroupsReady?: boolean,
  currentGroupId?: number | string,
  groups?: {
    [number | string]: FriendGroup,
  },
  history: Object,
  isLoadingGroups?: boolean,
  onOpenChangeDone: (boolean) => void,
  open?: boolean,
};

const Carousel = glamorous(AnimatedGroup)(({ open, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: open ? 1 : 0,
  flexShrink: 0,
  marginBottom: open ? 0 : 10,
  [theme.mediaQuery.desktopAndUp]: {
    marginBottom: 0,
  },
}));

class GroupCardCarousel extends Component<Props> {
  props: Props;

  static defaultProps = {
    allFriends: [],
    areGroupsReady: false,
    currentGroupId: undefined,
    groups: {},
    isLoadingGroups: false,
    open: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.props.open) {
      // if the group no longer exists in groups
      // we must've deleted it - set a different current group
      if (isNumeric(nextProps.currentGroupId) && !nextProps.groups[nextProps.currentGroupId]) {
        if (!some(nextProps.groups)) {
          this.props.history.push(`/groups/${FRIEND_GROUP_ID}`);
        } else {
          const deletedIndex = Object.values(this.props.groups).findIndex(g => g.id === nextProps.currentGroupId);
          const nextGroups = Object.values(nextProps.groups);
          const nextGroup = nextGroups[Math.min(deletedIndex, nextGroups.length - 1)];
          if (nextGroup) {
            this.props.history.push(`/groups/${nextGroup.id}`);
          }
        }
      }
    }
  }

  handleCardClick = (id: number) => {
    this.props.history.push(`/groups/${id}`);
  }

  render() {
    const {
      areGroupsReady,
      currentGroupId,
      groups,
      isLoadingGroups,
      onOpenChangeDone,
      open,
    } = this.props;
    const isNew = currentGroupId === NEW_GROUP_ID;
    const arrayGroups: Array<FriendGroup> = Object.values(groups);

    return (
      <Responsive mobile tabletPortrait tabletLandscape={false}>
        {isMobile => (
          <Carousel open={open}>
            {isLoadingGroups && !isNew && (
              <AnimatedGroupChild
                onEnter={Enter.appear()}
                onLeave={Leave.disappear()}
                key="loading"
              >
                <FriendLoader showOverlay>
                  {`${t('Fetching your groups')}...`}
                </FriendLoader>
              </AnimatedGroupChild>
            )}
            {areGroupsReady && !isNew && (
              isMobile ? (
                <GroupCardsMobile
                  key="mobile"
                  currentGroupId={currentGroupId}
                  onCardClick={this.handleCardClick}
                  groups={arrayGroups}
                  open={open}
                  onOpenChangeDone={onOpenChangeDone}
                />
              ) : (
                <GroupCardsDesktop
                  key="desktop"
                  groups={arrayGroups}
                  currentGroupId={currentGroupId}
                  onCardClick={this.handleCardClick}
                  onOpenChangeDone={onOpenChangeDone}
                  open={open}
                />
              )
            )}
          </Carousel>
        )}
      </Responsive>
    );
  }
}


const mapStateToProps = (state: GlobalState) => ({
  areGroupsReady: state.friendGroups.hasInitialised,
  groups: state.friendGroups.groups,
  isLoadingGroups: state.friendGroups.isFetchingGroups,
});

export default withRouter(connect(mapStateToProps, null)(GroupCardCarousel));
